import { MemberDto } from '@services/api'
import { removeMemberRoles } from '@state/reducers/roleReducer'
import { useSelector } from '@state/store'
import {
  assignMemberRoles,
  fetchMemberRoles,
  findAllRoles,
} from '@state/thunks/roleThunk'
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Shield, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

type AssignRoleToMemberFormProps = {
  onClose: () => void
  row?: MemberDto
}

export default function AssignRoleToMemberFrom({
  row,
  onClose,
}: AssignRoleToMemberFormProps) {
  const dispatch = useDispatch()
  const theme = useTheme()

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null)
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])

  const { sites, roles, memberRoles } = useSelector(({ site, role }) => ({
    sites: site.sites.datas,
    roles: role.roles,
    memberRoles: role.memberRoles,
  }))

  useEffect(() => {
    dispatch(findAllRoles() as any)
  }, [dispatch])

  useEffect(() => {
    if (row?.id) {
      dispatch(fetchMemberRoles(row.id) as any)
    }
    return () => {
      dispatch(removeMemberRoles())
    }
  }, [row?.id, dispatch])

  // Pre-populate roles when site changes
  useEffect(() => {
    if (!selectedSiteId || !memberRoles) {
      setSelectedRoleIds([])
      return
    }
    const siteRoles = memberRoles.sites.find((s) => s.siteId === selectedSiteId)
    setSelectedRoleIds(siteRoles?.roles.map((r) => r.id) ?? [])
  }, [selectedSiteId, memberRoles])

  const siteOptions = sites.map((s) => ({
    label: s.label,
    value: s.id,
  }))

  // All roles for the selected site
  const siteRoles = roles.filter((r) => r.siteId === selectedSiteId)

  // Only show roles NOT already selected in the dropdown
  const availableRoleOptions = siteRoles.filter(
    (r) => !selectedRoleIds.includes(r.id),
  )

  // Resolved selected role objects for chip display
  const selectedRoleObjects = siteRoles.filter((r) =>
    selectedRoleIds.includes(r.id),
  )

  const handleAddRole = (roleId: number) => {
    if (!selectedRoleIds.includes(roleId)) {
      setSelectedRoleIds((prev) => [...prev, roleId])
    }
  }

  const handleRemoveRole = (roleId: number) => {
    setSelectedRoleIds((prev) => prev.filter((id) => id !== roleId))
  }

  const handleSubmit = () => {
    if (!row?.id || !selectedSiteId) return
    dispatch(
      assignMemberRoles({
        memberId: row.id,
        body: { siteId: selectedSiteId, roleIds: selectedRoleIds },
        onSuccess: onClose,
      }) as any,
    )
  }

  const canSubmit = !!selectedSiteId && !!row?.id

  const initials = `${row?.firstName?.[0] ?? ''}${row?.lastName?.[0] ?? ''}`

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(theme.palette.secondary.main, 0.12),
              color: theme.palette.secondary.main,
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
              Assigner des rôles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row?.firstName} {row?.lastName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Site select */}
          <FormControl fullWidth size="small">
            <InputLabel>Site</InputLabel>
            <Select
              value={selectedSiteId ?? ''}
              onChange={(e) => setSelectedSiteId(Number(e.target.value))}
              label="Site"
            >
              {siteOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedSiteId && (
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Ajouter un rôle</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    if (val) handleAddRole(val)
                  }}
                  label="Ajouter un rôle"
                  disabled={availableRoleOptions.length === 0}
                  displayEmpty
                >
                  {availableRoleOptions.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        Aucun rôle disponible
                      </Typography>
                    </MenuItem>
                  ) : (
                    availableRoleOptions.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {selectedRoleObjects.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Rôles assignés ({selectedRoleObjects.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedRoleObjects.map((role) => (
                      <Chip
                        key={role.id}
                        label={role.name}
                        size="small"
                        icon={
                          <Shield
                            size={13}
                            style={{ marginLeft: 6 }}
                          />
                        }
                        deleteIcon={
                          <X
                            size={13}
                            style={{ marginRight: 4 }}
                          />
                        }
                        onDelete={() => handleRemoveRole(role.id)}
                        sx={{
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          color: theme.palette.secondary.dark,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                          '& .MuiChip-icon': {
                            color: theme.palette.secondary.main,
                          },
                          '& .MuiChip-deleteIcon': {
                            color: alpha(theme.palette.secondary.main, 0.5),
                            '&:hover': {
                              color: theme.palette.error.main,
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedRoleObjects.length === 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.disabled">
                    Aucun rôle assigné sur ce site
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} size="small">
          Annuler
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!canSubmit}
          onClick={handleSubmit}
          size="small"
        >
          Enregistrer
        </Button>
      </DialogActions>
    </>
  )
}